// Auth slice — tokens plus the access data (roles/permissions/user) the
// select-company response returns. This is the canonical source for gating, so
// the frontend no longer decodes the authToken. Persisted (via the root persist
// config) so a reload keeps the session. The axios base query reads tokens here.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface AuthRole {
  id: string;
  name: string;
  isOwner: boolean;
}

/** One module grant. `type` splits web-admin (`admin`) from mobile (`user`). */
export interface AuthPermission {
  module: string; // module slug, e.g. 'company_structure'
  type: 'admin' | 'user';
  permissions: string[]; // granted action verbs, e.g. ['view','create']
}

export interface AuthUser {
  firstName: string;
  lastName: string;
  countryCode: string;
  mobileNumber: string;
  email: string | null;
}

export interface CompanyDetail {
  _id: string;
  companyName: string;
  /** Human-readable company code — the same value the companies list returns
   *  on its rows (see CompanyItem in api/endpoints/companyApi.ts). */
  companyCode: string;
}

export interface AuthState {
  authToken: string | null;
  refreshToken: string | null;
  loginToken: string | null;
  loginRefreshToken: string | null;
  /** OTP-verification token; the only auth material for reset-password. */
  validationKey: string | null;
  companyId: string | null;
  companyDetail: CompanyDetail | null;
  roles: AuthRole[] | null;
  permissions: AuthPermission[] | null;
  user: AuthUser | null;
  /** The current user's own company-user record id — always returned by
   *  select-company, so null only before a company is selected. */
  companyUserId: string | null;
  /** The company owner's company-user record id (GET companies/owner-details). */
  ownerCompanyUserId: string | null;
}

const initialState: AuthState = {
  authToken: null,
  refreshToken: null,
  loginToken: null,
  loginRefreshToken: null,
  validationKey: null,
  companyId: null,
  companyDetail: null,
  roles: null,
  permissions: null,
  user: null,
  companyUserId: null,
  ownerCompanyUserId: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Merge in whatever tokens/access data a request returned. */
    setAuth: (state, action: PayloadAction<Partial<AuthState>>) => {
      Object.assign(state, action.payload);
    },
    clearAuth: () => initialState,
  },
});

export const { setAuth, clearAuth } = authSlice.actions;
export default authSlice;
